import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageHeader, Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { PermissionGate } from '../../components/PermissionGate'
import { rolesApi } from '../../api/roles.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import type { Role } from '../../api/types'
import { RoleFormModal } from './RoleFormModal'

export function RolesPage() {
  const queryClient = useQueryClient()
  const [formRole, setFormRole] = useState<Role | null | undefined>(undefined)
  const [deleteRole, setDeleteRole] = useState<Role | null>(null)

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: async () => (await rolesApi.list()).data })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => rolesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast.success('Rolle gelöscht.')
      setDeleteRole(null)
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Löschen fehlgeschlagen.')
      setDeleteRole(null)
    },
  })

  return (
    <div>
      <PageHeader
        title="Rollen"
        subtitle="Granulare Rechte, frei kombinierbar. Ein Benutzer kann mehrere Rollen tragen."
        actions={
          <PermissionGate anyOf={['roles.manage']}>
            <Button variant="primary" onClick={() => setFormRole(null)}>
              + Neue Rolle
            </Button>
          </PermissionGate>
        }
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 'var(--space-4)',
        }}
      >
        {rolesQuery.data?.map((role) => (
          <Card key={role.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: role.color ?? 'var(--accent-9)',
                  flexShrink: 0,
                }}
              />
              <div style={{ fontWeight: 600, fontSize: 'var(--text-base)' }}>{role.name}</div>
              {role.isSystem ? <Badge variant="accent">System</Badge> : null}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', minHeight: 36 }}>
              {role.description || 'Keine Beschreibung.'}
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-3)' }}>
              <span>{role.permissions.length} Rechte</span>
              <span>·</span>
              <span>{role._count.users} Benutzer</span>
            </div>
            <PermissionGate anyOf={['roles.manage']}>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button size="sm" variant="secondary" onClick={() => setFormRole(role)}>
                  Bearbeiten
                </Button>
                {!role.isSystem ? (
                  <Button size="sm" variant="danger" onClick={() => setDeleteRole(role)}>
                    Löschen
                  </Button>
                ) : null}
              </div>
            </PermissionGate>
          </Card>
        ))}
      </div>

      {formRole !== undefined ? <RoleFormModal role={formRole} onClose={() => setFormRole(undefined)} /> : null}
      {deleteRole ? (
        <ConfirmDialog
          title="Rolle löschen"
          message={`Rolle "${deleteRole.name}" wirklich löschen? Benutzer verlieren die damit verbundenen Rechte.`}
          danger
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deleteRole.id)}
          onCancel={() => setDeleteRole(null)}
        />
      ) : null}
    </div>
  )
}
